import React, { useCallback, useEffect, useState } from 'react'
import { atom, useAtom } from 'jotai'
import { useTheme, createUseStyles } from 'react-jss'
import { Root, Portal, Content, Overlay, Close } from '@radix-ui/react-dialog'
import ReactQuill from 'react-quill'
import { Overlay as Sidebar } from '../../../ui/Sidebar'
import { getMailTemplateList, sendMail } from '../../../../api/salary'
import { FormControl } from '../../../ui/FormElememts/FormControl'
import { Select } from '../../../ui/FormElememts/Select'
import { FormInput } from '../../../modules/FormElements/FormInput'
import 'react-quill/dist/quill.snow.css'
import { useStyles as useDialogStyles } from '../../../ui/Dialog/Dialog'
import {
    I23IkonPromptKontaktarbejdsgiver,
    IkonFilliste,
    IkonKontaktarbejdsgiver,
    IkonMailsendt,
    IkonPromptfejl,
    Close as CloseIcon,
    IkonUdraabstegn,
} from '../../../../assets/icons'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { CaseDetail, File } from '../../../../models/CaseDetail'
import { profileAtom } from '../../../../api/profile/profile'
import { FormMessage } from '../../../ui/FormElememts/FormMessage'
import { L, L_HTML } from '../../../providers/Lokalise'
import { useErrorDialog } from '../../../../hooks/useErrorDialog'
import { RestrictElementWithTooltip } from '../RestrictElementWithTooltip'
import { useWindowResize } from '../../../../hooks/useWindowSize'
import breakpoints from '../../../../theme/breakpoints'
import { RemindersHeaderInfo } from '../Reminders/Reminders'
import { Accordion } from '../../../ui/Accordion/Accordion'
import { accordionWrapper } from '../../../../theme/reuseables'
import Checkbox from '../../../ui/FormElememts/Checkbox'
import { useFileDownload } from '../../../../hooks/useFileDownload'
import { formatDate } from '../../../../utils/format'
import { Typography } from '../../../ui/Typography/Typography'
import { Circle } from '../../../ui/Card'
import { CircleIcon } from '../../../ui/Circle/Circle'

// Local styles for ContactDialog component
interface ContactDialogStyleProps {
    iconBGColor?: string
    iconBorder?: string
}

type ContactDialogRuleNames =
    | 'customContainer'
    | 'contentWrapper'
    | 'accordionWrapper'
    | 'button'

const useContactStyles = createUseStyles<
    ContactDialogRuleNames,
    ContactDialogStyleProps,
    Jss.Theme
>({
    customContainer: {
        padding: '1rem',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        marginBottom: '1rem',
    },
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: '100%',
        paddingBottom: '80px',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
    },
    accordionWrapper: {
        ...accordionWrapper,
    },
    button: {
        position: 'absolute',
        right: 0,
        top: 0,
        display: 'inline-block',
        padding: '0.75em 1.75em',
        margin: '0 0.3em 0.3em 0',
        borderRadius: '0.25em',
        boxSizing: 'border-box',
        textDecoration: 'none',
        fontWeight: 400,
        textAlign: 'center',
        transition: 'all 0.2s',
        background: 'transparent',
        boxShadow: 0,
        '&:hover': {
            cursor: 'pointer',
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        border: 0,
        '&:hover svg': {
            fill: colors.gray500,
        },
    },
})

type ContactDialogProps = {
    currentCase: CaseDetail
    onUpdated?: () => void
    onError?: (error: string) => void
}

const editorStyles = `
    .ql-container {
        height: 200px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background-color: white;
        font-size: 14px;
        overflow-y: auto;
    }
`

type EditorProps = {
    defaultValue: string
    onChange: (value: string) => void
    hasError?: boolean
}

type ContactDialogStateProps = {
    caseId: string
    template: string
    content: string
    selectedFileList: File[]
    to: string
    subject: string
    cc: string
    bcc: string
}

export const initialContactDialogState: ContactDialogStateProps = {
    caseId: '',
    template: '',
    content: '',
    selectedFileList: [],
    to: '',
    subject: '',
    cc: '',
    bcc: '',
}

export const contactDialogStateAtom = atom<ContactDialogStateProps>(
    initialContactDialogState
)

// Atom to control opening ContactDialog from external components
export const openContactDialogAtom = atom<boolean>(false)

export const Editor: React.FC<EditorProps> = ({
    defaultValue,
    onChange,
    hasError,
}): JSX.Element => {
    const [value, setValue] = useState(defaultValue)

    useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue)
        }
    }, [defaultValue])

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }],
            ['clean'],
        ],
    }

    return (
        <div
            style={{
                color: colors.black,
                fontSize: '20px',
                border: hasError ? `1px solid ${colors.error}` : 'none',
            }}
        >
            <style>{editorStyles}</style>
            <ReactQuill
                value={value}
                onChange={(newVal) => {
                    setValue(newVal)
                    onChange(newVal)
                }}
                modules={modules}
                theme="snow"
            />
        </div>
    )
}

// Utility function to extract all error messages from an error object
function extractErrorMessages(errors: Record<string, string[]>): string[] {
    if (!errors || typeof errors !== 'object') return []
    return Object.values(errors).flat()
}

// Component to display errors with their keys as headers
export const ErrorDisplay: React.FC<{ errors: Record<string, string[]> }> = ({
    errors,
}) => {
    if (!errors || typeof errors !== 'object') return null
    return (
        <div>
            {Object.entries(errors).map(([key, messages]) => (
                <div key={key} style={{ marginBottom: '1rem' }}>
                    <strong>{key}</strong>
                    {messages.map((msg) => (
                        <p key={msg} style={{ margin: 0 }}>
                            {msg}
                        </p>
                    ))}
                </div>
            ))}
        </div>
    )
}

type CustomErrorsProps = {
    template?: {
        message: string
    }
    to?: {
        message: string
    }
    cc?: {
        message: string
    }
    bcc?: {
        message: string
    }
    subject?: {
        message: string
    }
    content?: {
        message: string
    }
    attachments?: {
        message: string
    }
}

const ContactDialog: React.FC<ContactDialogProps> = ({
    currentCase,
    onUpdated,
    onError,
}) => {
    const [templateList, setTemplateList] = useState<any[]>([])
    const [template, setTemplate] = useState<any>(null)
    const [content, setContent] = useState<string>('')
    const [options, setOptions] = useState<any[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [openContactDialog, setOpenContactDialog] = useAtom(
        openContactDialogAtom
    )
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white
    const [authProfile] = useAtom(profileAtom)
    const [selectedFileList, setSelectedFileList] = useState<File[]>([])
    const [to, setTo] = useState<string>('')
    const [cc, setCc] = useState<string>('')
    const [bcc, setBcc] = useState<string>('')
    const [subject, setSubject] = useState<string>('')
    const windowSize = useWindowResize()
    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isSent, setIsSent] = useState<boolean>(false)
    const [customErrorMessage, setCustomErrorMessage] = useState<
        CustomErrorsProps | undefined
    >(undefined)
    const [contactDialogState, setContactDialogState] = useAtom(
        contactDialogStateAtom
    )
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const { isLoading: isDownloading, openFile } = useFileDownload()

    const dialogClasses = useDialogStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    const contactClasses = useContactStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    const handleDownload = async (item: File): Promise<void> => {
        await openFile({
            caseNumber: item.caseNumber,
            fullName: item.fullName,
            extension: item.extension,
        })
    }

    const [showNoAttachmentConfirm, setShowNoAttachmentConfirm] =
        useState(false)

    useEffect(() => {
        if (!isOpen) {
            setShowNoAttachmentConfirm(false)
        }
    }, [isOpen])

    const sendMailRequest = async (): Promise<void> => {
        const toData = to
        const subjectData = subject
        const mailContent = content

        try {
            setIsLoading(true)
            const response = await sendMail({
                subject: String(subjectData),
                content: mailContent,
                to: String(toData)
                    .split(',')
                    .map((email) => email.trim())
                    .filter((email) => email.length > 0),
                template: template?.skabelonType,
                cc: String(cc)
                    .split(',')
                    .map((email) => email.trim())
                    .filter((email) => email.length > 0),
                bcc: String(bcc)
                    .split(',')
                    .map((email) => email.trim())
                    .filter((email) => email.length > 0),
                id: currentCase.caseId,
                attachments: selectedFileList.map((item) => ({
                    fileName: item.fullName,
                    caseNumber: item.caseNumber,
                })),
            })
            if (response.error) {
                showError(<ErrorDisplay errors={response.error.errors} />)
                setIsLoading(false)
                return
            }
            setIsLoading(false)
            setIsSent(true)
        } catch (error) {
            console.error('Der skete en fejl - kontakt support', error)
            setOpenErrorDialog(true)
            setErrorMessage('Der skete en fejl - kontakt support')
            onError?.('Der skete en fejl - kontakt support')
            setIsLoading(false)
        }
    }

    const handleSubmit = async (): Promise<void> => {
        // e.preventDefault()

        const toData = to
        const subjectData = subject
        const mailContent = content

        // Collect all validation errors so every invalid field is flagged at
        // once, instead of surfacing them one at a time.
        const errors: CustomErrorsProps = {}

        if (!template) {
            errors.template = { message: 'Du skal vælge en skabelon' }
        }

        if (!toData) {
            errors.to = { message: 'Du skal vælge en modtager' }
        }

        if (!subjectData) {
            errors.subject = { message: 'Du skal skrive et emne' }
        }

        if (!mailContent) {
            errors.content = { message: 'Du skal skrive en besked' }
        }

        if (Object.keys(errors).length > 0) {
            setCustomErrorMessage(errors)
            return
        }

        setCustomErrorMessage(undefined)

        const caseHasFiles = (currentCase.fileList?.length ?? 0) > 0
        if (selectedFileList.length === 0 && caseHasFiles) {
            setShowNoAttachmentConfirm(true)
            return
        }

        await sendMailRequest()
    }

    const renderNoAttachmentConfirmDialog = (): JSX.Element | null => {
        if (!isOpen || !showNoAttachmentConfirm) {
            return null
        }

        const stackZ = { overlay: 50, content: 51 }

        return (
            <Root
                open
                onOpenChange={(nextOpen) => {
                    if (!nextOpen) {
                        setShowNoAttachmentConfirm(false)
                    }
                }}
            >
                <Portal>
                    <Overlay
                        className={dialogClasses.overlay}
                        style={{ zIndex: stackZ.overlay }}
                    />
                    <Content
                        className={dialogClasses.overlayContainer}
                        style={{ zIndex: stackZ.content }}
                    >
                        <div
                            className={`${dialogClasses.container} withIcon`}
                            style={{
                                maxWidth: '480px',
                                paddingBottom: 0,
                                paddingLeft: '2rem',
                                paddingRight: '2rem',
                            }}
                        >
                            <div
                                className={`${dialogClasses.circleIcon} withIcon`}
                            >
                                <IkonUdraabstegn
                                    width="36px"
                                    fill={colors.green}
                                />
                            </div>
                            <Close
                                className={contactClasses.button}
                                onClick={() => {
                                    setShowNoAttachmentConfirm(false)
                                }}
                            >
                                <CloseIcon width="32px" />
                            </Close>
                            <Typography
                                tag="h2"
                                size="M"
                                align="center"
                                style={{ marginBottom: '1rem' }}
                            >
                                {L_HTML(
                                    'LØN_KONTAKT_ARBEJDSGIVER_DIALOG_UDEN_FIL_TITLE'
                                )}
                            </Typography>
                            <Typography tag="p" size="S" align="center">
                                {L_HTML(
                                    'LØN_KONTAKT_ARBEJDSGIVER_DIALOG_UDEN_FIL_TEKST'
                                )}
                            </Typography>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    gap: '.5rem',
                                    marginTop: '2rem',
                                    marginBottom: '2rem',
                                    width: '100%',
                                }}
                            >
                                <Button
                                    buttonType="button"
                                    type="secondary"
                                    loading={isLoading}
                                    disabled={isLoading}
                                    onClick={async () => {
                                        setShowNoAttachmentConfirm(false)
                                        await sendMailRequest()
                                    }}
                                >
                                    {L_HTML(
                                        'LØN_KONTAKT_ARBEJDSGIVER_DIALOG_UDEN_FIL_SEND_UDEN_KNAP'
                                    )}
                                </Button>
                                <Button
                                    buttonType="button"
                                    type="primary"
                                    onClick={() =>
                                        setShowNoAttachmentConfirm(false)
                                    }
                                >
                                    {L_HTML(
                                        'LØN_KONTAKT_ARBEJDSGIVER_DIALOG_UDEN_FIL_VEDHÆFT_KNAP'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Content>
                </Portal>
            </Root>
        )
    }

    const handleFileChange = (value: File): void => {
        const file = currentCase.fileList.find(
            (item) =>
                item.fullName === value?.fullName &&
                item.caseNumber === value?.caseNumber
        )
        const found = selectedFileList.find(
            (item) =>
                item.fullName === file?.fullName &&
                item.caseNumber === file?.caseNumber
        )
        // Add file to selectedFileList or remove it if it already exists
        if (file) {
            if (found) {
                setSelectedFileList(
                    selectedFileList.filter(
                        (item) =>
                            item.fullName !== file.fullName ||
                            item.caseNumber !== file.caseNumber
                    )
                )
            } else {
                setSelectedFileList([...selectedFileList, file])
            }
        }
    }

    useEffect(() => {
        if (!isOpen) {
            // Only fetch when the dialog is actually open
            return
        }

        const fetchMailTemplateList = async (): Promise<void> => {
            const mailTemplateList = await getMailTemplateList(
                currentCase.caseId
            )

            setTemplateList(mailTemplateList)
            setOptions(
                mailTemplateList?.map((currentTemplate) => ({
                    label: currentTemplate.skabelonNavn,
                    value: currentTemplate.skabelonNavn,
                }))
            )
        }

        fetchMailTemplateList()
    }, [isOpen, currentCase.caseId])

    useEffect(() => {
        if (contactDialogState.caseId !== currentCase.caseId) {
            setTemplate(null)
            setContent('')
            setSelectedFileList([])
            setTo('')
            setCc('')
            setBcc('')
            setSubject('')
        }
    }, [contactDialogState])

    const saveDialogState = (): void => {
        const state: ContactDialogStateProps = {
            caseId: currentCase.caseId,
            template: template?.skabelonNavn,
            content,
            selectedFileList,
            to,
            subject,
            cc,
            bcc,
        }

        setContactDialogState(state)
    }

    const loadDialogState = useCallback((): void => {
        if (contactDialogState.caseId === currentCase.caseId) {
            // Find and set the template from the template list
            const savedTemplate = templateList.find(
                (t) => t.skabelonNavn === contactDialogState.template
            )
            setTemplate(savedTemplate)
            setContent(contactDialogState.content)
            setSelectedFileList(contactDialogState.selectedFileList)
            setTo(contactDialogState.to)
            setCc(contactDialogState.cc)
            setBcc(contactDialogState.bcc)
            setSubject(contactDialogState.subject)
            // Set form values after a brief delay to ensure form is rendered
            setTimeout(() => {
                const form = document.querySelector(
                    'form[noValidate]'
                ) as HTMLFormElement
                if (form) {
                    const tilInput = form.querySelector(
                        'input[name="til"]'
                    ) as HTMLInputElement
                    const emneInput = form.querySelector(
                        'input[name="emne"]'
                    ) as HTMLInputElement

                    if (tilInput && contactDialogState.to) {
                        tilInput.value = contactDialogState.to
                    }
                    if (emneInput && contactDialogState.subject) {
                        emneInput.value = contactDialogState.subject
                    }
                }
            }, 100)
        } else {
            setTemplate(null)
            setContent('')
            setSelectedFileList([])
            setContactDialogState(initialContactDialogState)
        }
    }, [
        contactDialogState,
        currentCase.caseId,
        templateList,
        setContactDialogState,
    ])

    // Listen for external open requests via the atom
    useEffect(() => {
        if (openContactDialog) {
            setIsOpen(true)
            loadDialogState()
            setOpenContactDialog(false)
        }
    }, [openContactDialog, loadDialogState, setOpenContactDialog])

    const renderFileSelection = (): JSX.Element => {
        return (
            <div className={contactClasses.accordionWrapper}>
                <Accordion
                    variant="select"
                    title={L('LØN_KONTAKT_ARBEJDSTAGER_DIALOG_FIL_PLACEHOLDER')}
                >
                    <>
                        {currentCase?.fileList?.map((option) => (
                            <div
                                key={`${option.fullName}-${option.caseNumber}`}
                                style={{
                                    paddingTop: '1rem',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '1rem',
                                }}
                            >
                                <div style={{ flex: '1', minWidth: 0 }}>
                                    <Checkbox
                                        onChange={() => {
                                            handleFileChange(option)
                                        }}
                                        id={`${option.fullName}-${option.caseNumber}`}
                                        name={`${option.fullName}-${option.caseNumber}`}
                                        label={option.fullName}
                                        isChecked={
                                            !!selectedFileList.find(
                                                (item) =>
                                                    item.fullName ===
                                                        option.fullName &&
                                                    item.caseNumber ===
                                                        option.caseNumber
                                            )
                                        }
                                        labelStyle={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '100%',
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '0.5rem',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        tag="span"
                                        size="XXS"
                                        color={colors.gray800}
                                    >
                                        {`Tilføjet d. ${formatDate(
                                            new Date(option.changeTime)
                                        )}`}
                                    </Typography>
                                    <Button
                                        type="action"
                                        onClick={() => handleDownload(option)}
                                        size="xlarge"
                                        style={{
                                            fontSize: '.9rem',
                                            minHeight: '1px',
                                            textDecoration: 'underline',
                                        }}
                                        loading={isDownloading}
                                        disabled={isDownloading}
                                    >
                                        {L(
                                            'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_VIS_FIL'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </>
                </Accordion>
            </div>
        )
    }

    const renderSendMailView = (): JSX.Element => {
        return (
            <>
                <div
                    className={`${dialogClasses.circleIcon} withIcon desktop-only`}
                >
                    <I23IkonPromptKontaktarbejdsgiver
                        width="36px"
                        fill={colors.green}
                    />
                </div>
                <div
                    style={{
                        overflow: 'auto',
                        maxWidth: '100%',
                        width: '100%',
                        textAlign: 'left',
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            maxWidth: '100%',
                            padding: '0 0 1rem',
                        }}
                        // noValidate
                    >
                        <Typography
                            tag="h2"
                            size="M"
                            align="center"
                            style={{
                                marginBottom: '2rem',
                            }}
                        >
                            {/* Send mail til arbejdsgiver */}
                            {L('LØN_KONTAKT_ARBEJDSTAGER_DIALOG_TITEL')}
                        </Typography>
                        <FormControl>
                            <Select
                                options={options}
                                placeholder={`${L(
                                    'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_SKABELON_PLACEHOLDER'
                                )} *`}
                                defaultValue={template?.skabelonNavn}
                                hasError={
                                    !!customErrorMessage?.template?.message
                                }
                                onChange={(value) => {
                                    setCustomErrorMessage(undefined)
                                    const selectedTemplate = templateList.find(
                                        (currentTemplate) =>
                                            currentTemplate.skabelonNavn ===
                                            value.target.value
                                    )

                                    setTemplate(selectedTemplate)
                                    setContent(selectedTemplate?.indhold)
                                    setSelectedFileList([])
                                    setTo(
                                        selectedTemplate?.tilEmailAdresseList.join(
                                            ', '
                                        )
                                    )
                                    setSubject(selectedTemplate?.emne)
                                }}
                            />
                            <FormMessage
                                text={
                                    customErrorMessage?.template?.message ?? ''
                                }
                                error={customErrorMessage?.template?.message}
                            />
                        </FormControl>

                        <hr
                            style={{
                                margin: '1rem 0 2rem',
                                borderColor: colors.borderColor,
                                borderWidth: '1px',
                                borderStyle: 'solid',
                            }}
                        />

                        <FormControl>
                            <FormInput
                                label={`${L(
                                    'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_TIL_LABEL'
                                )}`}
                                name="til"
                                required
                                type="email"
                                hasError={!!customErrorMessage?.to?.message}
                                defaultValue={to}
                                onChange={(value) => {
                                    setTo(value.target.value)
                                }}
                                value={to}
                                rules={{
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: 'indtast en gyldig email',
                                    },
                                }}
                            />
                            <FormMessage
                                text={customErrorMessage?.to?.message ?? ''}
                                error={customErrorMessage?.to?.message}
                            />
                        </FormControl>

                        <FormControl>
                            <FormInput
                                label={`${L(
                                    'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_CC_LABEL'
                                )}`}
                                name="cc"
                                type="email"
                                hasError={!!customErrorMessage?.cc?.message}
                                defaultValue={cc}
                                onChange={(value) => {
                                    setCc(value.target.value)
                                }}
                                value={cc}
                                required={false}
                                rules={{
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: 'indtast en gyldig email',
                                    },
                                }}
                            />
                            <FormMessage
                                text={customErrorMessage?.cc?.message ?? ''}
                                error={customErrorMessage?.cc?.message}
                            />
                        </FormControl>

                        <FormControl>
                            <FormInput
                                label={`${L(
                                    'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_BCC_LABEL'
                                )}`}
                                name="bcc"
                                required={false}
                                type="email"
                                hasError={!!customErrorMessage?.bcc?.message}
                                defaultValue={bcc}
                                onChange={(value) => {
                                    setBcc(value.target.value)
                                }}
                                value={bcc}
                                rules={{
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: 'indtast en gyldig email',
                                    },
                                }}
                            />
                            <FormMessage
                                text={customErrorMessage?.bcc?.message ?? ''}
                                error={customErrorMessage?.bcc?.message}
                            />
                        </FormControl>

                        <FormControl>
                            <FormInput
                                label={`${L(
                                    'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_EMNE_LABEL'
                                )}`}
                                name="emne"
                                required
                                defaultValue={subject}
                                value={subject}
                                onChange={(value) => {
                                    setSubject(value.target.value)
                                }}
                                hasError={
                                    !!customErrorMessage?.subject?.message
                                }
                            />
                            <FormMessage
                                text={
                                    customErrorMessage?.subject?.message ?? ''
                                }
                                error={customErrorMessage?.subject?.message}
                            />
                        </FormControl>

                        <FormControl>
                            {renderFileSelection()}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '.5rem',
                                    marginTop: '1rem',
                                }}
                            >
                                {selectedFileList.map((item) => (
                                    <Button
                                        key={item.fullName}
                                        buttonType="button"
                                        type="ghost"
                                        onClick={() => {
                                            handleFileChange(item)
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: 'fit-content',
                                            padding: '0.5rem 1rem',
                                            textAlign: 'center',
                                            backgroundColor: 'transparent',
                                            color: colors.black,
                                            border: `1px solid ${colors.borderColor}`,
                                            borderRadius: '16px',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        <IkonFilliste
                                            fill={colors.green500}
                                            width="20px"
                                        />
                                        <Typography tag="span" size="XXS">
                                            {item.fullName}
                                        </Typography>
                                        <div>
                                            <div
                                                style={{
                                                    backgroundColor:
                                                        colors.borderColor,
                                                    borderRadius: '50%',
                                                    width: '16px',
                                                    height: '16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <IkonPromptfejl
                                                    width="10px"
                                                    height="10px"
                                                    fill={colors.white}
                                                />
                                            </div>
                                        </div>
                                    </Button>
                                ))}
                            </div>
                        </FormControl>

                        <FormControl>
                            <Editor
                                hasError={
                                    !!customErrorMessage?.content?.message
                                }
                                defaultValue={content}
                                onChange={(value) => {
                                    setContent(value)
                                }}
                            />
                            <FormMessage
                                text={
                                    customErrorMessage?.content?.message ?? ''
                                }
                                error={customErrorMessage?.content?.message}
                            />
                        </FormControl>

                        <FormControl>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '.5rem',
                                }}
                            >
                                <Button
                                    buttonType="button"
                                    type="secondary"
                                    onClick={() => {
                                        saveDialogState()
                                        setIsOpen(false)
                                    }}
                                >
                                    {L(
                                        'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_ANNULLERE_BUTTON'
                                    )}
                                </Button>
                                <Button
                                    buttonType="submit"
                                    type="primary"
                                    loading={isLoading}
                                    disabled={isLoading}
                                    onClick={() => {
                                        handleSubmit()
                                    }}
                                >
                                    {L(
                                        'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_SEND_MAIL_BUTTON'
                                    )}
                                </Button>
                            </div>
                        </FormControl>
                    </div>
                </div>
            </>
        )
    }

    const renderSentMailView = (): JSX.Element => {
        return (
            <>
                <div className={`${dialogClasses.circleIcon} withIcon`}>
                    <IkonMailsendt width="36px" fill={colors.green} />
                </div>
                <Typography
                    tag="h2"
                    size="M"
                    align="center"
                    style={{
                        marginBottom: '2rem',
                    }}
                >
                    {L('LØN_KONTAKT_ARBEJDSTAGER_DIALOG_MAIL_SENT_TITLE')}
                </Typography>
                <Typography tag="p" size="S" align="center">
                    {L('LØN_KONTAKT_ARBEJDSTAGER_DIALOG_MAIL_SENT_TEXT')}
                </Typography>
                <Button
                    buttonType="button"
                    size="xlarge"
                    type="primary"
                    onClick={() => {
                        setIsOpen(false)
                        onUpdated?.()
                        setIsSent(false)
                        setErrorMessage('')
                        setOpenErrorDialog(false)
                        setTemplate(null)
                        setContent('')
                        setSelectedFileList([])
                    }}
                    style={{
                        marginTop: '3rem',
                        marginBottom: '4rem',
                    }}
                >
                    {L('LØN_KONTAKT_ARBEJDSTAGER_DIALOG_OK_BUTTON')}
                </Button>
            </>
        )
    }

    const renderDesktop = (): JSX.Element => {
        return (
            <Root open={isOpen}>
                <Portal>
                    <Overlay className={dialogClasses.overlay} />
                    <Content className={`${dialogClasses.overlayContainer}`}>
                        <div
                            className={`${dialogClasses.container} withIcon ${
                                isSent ? '' : 'large'
                            }`}
                            style={{
                                paddingBottom: 0,
                                paddingLeft: '2rem',
                                paddingRight: '2rem',
                            }}
                        >
                            {!isSent
                                ? renderSendMailView()
                                : renderSentMailView()}
                            {!isSent && (
                                <Close
                                    className={contactClasses.button}
                                    onClick={() => {
                                        saveDialogState()
                                        setIsOpen(false)
                                    }}
                                >
                                    <CloseIcon width="32px" />
                                </Close>
                            )}
                        </div>
                    </Content>
                </Portal>
            </Root>
        )
    }

    const renderMobile = (): JSX.Element => {
        return (
            <Sidebar
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                }}
            >
                <RemindersHeaderInfo
                    name={currentCase.newlyHired.name}
                    caseNumber={currentCase.caseNumber}
                />
                <div className={`${contactClasses.contentWrapper} visible`}>
                    {!isSent ? renderSendMailView() : renderSentMailView()}
                </div>
            </Sidebar>
        )
    }

    return (
        <>
            <RestrictElementWithTooltip
                restrictedAction={
                    !currentCase?.allowedUpdateActionList.includes(
                        'Sagsbehandle'
                    )
                }
            >
                <Button
                    type="primary"
                    onClick={() => {
                        setIsOpen(true)
                        loadDialogState()
                    }}
                    size="xxlarge"
                    style={{
                        paddingLeft: '1rem',
                        paddingRight: '1rem',
                        paddingBlock: '.75rem',
                        width: '100%',
                        justifyContent: 'start',
                    }}
                    disabled={
                        !currentCase?.allowedUpdateActionList.includes(
                            'Sagsbehandle'
                        ) || authProfile.isImitator
                    }
                >
                    <Circle>
                        <CircleIcon>
                            <IkonKontaktarbejdsgiver
                                fill={colors.green}
                                width="16px"
                                style={{ padding: '2px' }}
                            />
                        </CircleIcon>
                    </Circle>

                    <span style={{ marginRight: '.5rem' }}>
                        {L(
                            'LØN_KONTAKT_ARBEJDSTAGER_DIALOG_KONTAKT_ARBEJDSTAGER_BUTTON'
                        )}
                    </span>
                </Button>
            </RestrictElementWithTooltip>
            {windowSize.width < breakpoints.Desktop && renderMobile()}
            {windowSize.width >= breakpoints.Desktop && renderDesktop()}
            {renderNoAttachmentConfirmDialog()}
            <ErrorDialogComponent />
        </>
    )
}

export { ContactDialog }
